/**
 * Sessions Report Toolbar Component
 * Redesigned to match Figma design and Flutter implementation
 *
 * Top Row: Operator, Location, Location Usage, Start Source, Account Name, Period, View Report, Export
 * Bottom Row: Column Group Checkboxes, Live Transactions Toggle
 */

import { useState, useCallback, useEffect } from 'react';
import { RotateCcw } from 'lucide-react';
import { format } from 'date-fns';
import { VisibilityState } from '@tanstack/react-table';
import { Button } from '@/core/components/ui/button';
import { Checkbox } from '@/core/components/ui/checkbox';
import { Switch } from '@/core/components/ui/switch';
import { SessionFilters } from '../types/session-types';
import { ColumnGroup, columnGroups, availableColumns } from './SessionsGridColumns';
import { cn } from '@/core/lib/utils';
import {
  TextFilterInput,
  LocationUsageDropdown,
  StartSourceMultiSelect,
  DeviceTypeMultiSelect,
  PaymentMethodDropdown,
  DatePresetMenu,
} from './filters';
import type { LocationUsageType, StartSourceType } from '../types/filter-enums';
import { ActorSelector } from '@/core/components/ActorSelector';
import { LocationSelector } from '@/core/components/LocationSelector';
import { useOperatorStore } from '@/core/stores/operator-store';
import { useAuthStore } from '@/core/stores/auth-store';
import { ExportMenu } from './ExportMenu';

interface SessionsToolbarProps {
  filters: SessionFilters;
  onFiltersChange: (filters: Partial<SessionFilters>) => void;
  enabledColumnGroups: ColumnGroup[];
  onColumnGroupChange: (groupId: ColumnGroup, enabled: boolean) => void;
  totalCount?: number;
  isLoading?: boolean;
  // HIVE-6307: Export with column selection
  onExport?: (columnVisibility: VisibilityState) => void;
  columnVisibility?: VisibilityState;
  onRefresh?: () => void;
  onViewReport?: () => void;
  className?: string;
  showAdvancedFilters?: boolean;
  onToggleAdvancedFilters?: () => void;
  // Export state (HIVE-6245 #14)
  isExporting?: boolean;
  exportProgress?: { current: number; total: number } | null;
}

/**
 * Sessions Report Toolbar
 * Contains all filters and column group toggles per Figma design
 */
export function SessionsToolbar({
  filters,
  onFiltersChange,
  enabledColumnGroups,
  onColumnGroupChange,
  totalCount,
  isLoading = false,
  onExport,
  columnVisibility = {},
  onRefresh,
  onViewReport,
  className,
  showAdvancedFilters = false,
  onToggleAdvancedFilters,
  isExporting = false,
  exportProgress,
}: SessionsToolbarProps) {
  const [selectedLocationId, setSelectedLocationId] = useState<string>('');

  // Get operator data from store
  const { operators, selectedOperator, selectOperator, isLoading: operatorsLoading, loadOperators } = useOperatorStore();

  // Get distributor status from auth store (not based on operator count)
  const { isDistributor } = useAuthStore();

  // Load operators when component mounts if distributor and operators not loaded
  useEffect(() => {
    if (isDistributor && operators.length === 0 && !operatorsLoading) {
      loadOperators();
    }
  }, [isDistributor, operators.length, operatorsLoading, loadOperators]);

  const handleOperatorChange = useCallback((operatorId: string) => {
    const operator = operators.find(op => op.operatorId.toString() === operatorId);
    if (operator) {
      selectOperator(operator);
      // Clear location selection when operator changes
      setSelectedLocationId('');
      onFiltersChange({ actorIds: undefined });
    }
  }, [operators, selectOperator, onFiltersChange]);

  const handleLocationChange = useCallback((locationId: string) => {
    setSelectedLocationId(locationId);
    if (locationId) {
      onFiltersChange({ actorIds: [parseInt(locationId, 10)] });
    } else {
      onFiltersChange({ actorIds: undefined });
    }
  }, [onFiltersChange]);

  const handlePeriodChange = useCallback(
    (range: { from: string; to: string }) => {
      onFiltersChange({ dateFrom: range.from, dateTo: range.to });
      // Auto-refresh when period changes (per HIVE-6245 requirement #9)
      if (onViewReport) {
        onViewReport();
      }
    },
    [onFiltersChange, onViewReport]
  );

  const handleFilterChange = useCallback(
    <K extends keyof SessionFilters>(key: K, value: SessionFilters[K]) => {
      onFiltersChange({ [key]: value });
    },
    [onFiltersChange]
  );

  const handleLiveTransactionsChange = useCallback(
    (checked: boolean) => {
      onFiltersChange({ liveTransactions: checked });

      // Auto-disable Cost and Buy Info when Live Transactions is ON (HIVE-6300 Flutter behavior)
      if (checked) {
        if (enabledColumnGroups.includes('cost')) {
          onColumnGroupChange('cost', false);
        }
        if (enabledColumnGroups.includes('buyInfo')) {
          onColumnGroupChange('buyInfo', false);
        }
      }

      // Auto-refresh when Live Transactions toggle changes (per HIVE-6245 #9)
      if (onViewReport) {
        onViewReport();
      }
    },
    [onFiltersChange, onViewReport, enabledColumnGroups, onColumnGroupChange]
  );

  const handleResetFilters = useCallback(() => {
    const now = new Date();
    const lastWeek = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    onFiltersChange({
      dateFrom: format(lastWeek, 'yyyy-MM-dd'),
      dateTo: format(now, 'yyyy-MM-dd'),
      groupByLocation: false,
      creditCardLast4: undefined,
      actorIds: undefined,
      sessionId: undefined,
      chargingStation: undefined,        // HIVE-6245 #6: replaces chargePointName + chargePointNumber
      physicalReference: undefined,      // HIVE-6245 #7
      chargePointName: undefined,        // Deprecated but keep for backwards compat
      chargePointNumber: undefined,      // Deprecated but keep for backwards compat
      connectorId: undefined,
      nayaxTransactionId: undefined,
      accountName: undefined,
      locationUsage: undefined,
      startSources: undefined,
      deviceTypes: undefined,            // HIVE-6618: Device Type filter
      tokenId: undefined,
      paymentMethod: undefined,
      sessionHealth: undefined,
      liveTransactions: false,
      last4Digits: undefined,            // HIVE-6307: Last 4 Digits filter
    });
    // HIVE-6307: Auto-refresh after reset
    if (onViewReport) {
      // Use setTimeout to ensure filters are updated before refresh
      setTimeout(() => onViewReport(), 0);
    }
  }, [onFiltersChange, onViewReport]);

  return (
    <div className={cn('bg-white border-b border-[#E8E8E8] flex-shrink-0', className)}>
      {/* Row 1 - Primary Context Filters: Who, Where, When + Action */}
      <div className="flex flex-wrap items-end gap-3 px-4 py-2">
        {/* Operator Selector (only for distributors) */}
        {isDistributor && (
          <div className="flex flex-col gap-1">
            <span className="text-xs text-muted-foreground">Operator</span>
            <ActorSelector
              operators={operators}
              selectedOperatorId={selectedOperator?.operatorId?.toString() || ''}
              onOperatorChange={handleOperatorChange}
              isLoading={operatorsLoading}
              className="w-[160px] h-8 border-[#E8E8E8]"
            />
          </div>
        )}

        {/* Location Selector */}
        {selectedOperator && (
          <div className="flex flex-col gap-1">
            <span className="text-xs text-muted-foreground">Location</span>
            <LocationSelector
              operatorId={selectedOperator.operatorId}
              selectedLocationId={selectedLocationId}
              onLocationChange={handleLocationChange}
              className="w-[160px] h-8 border-[#E8E8E8]"
              autoSelectFirst={false}
            />
          </div>
        )}

        {/* Location Usage Multi-Select */}
        <LocationUsageDropdown
          value={filters.locationUsage}
          onChange={(value) => handleFilterChange('locationUsage', value)}
          onAutoRefresh={onViewReport}
          className="w-[130px]"
        />

        {/* Start Source Multi-Select */}
        <StartSourceMultiSelect
          value={filters.startSources || []}
          onChange={(value) =>
            handleFilterChange('startSources', value.length > 0 ? value : undefined)
          }
          onAutoRefresh={onViewReport}
          className="w-[140px]"
        />

        {/* Device Type Multi-Select - HIVE-6618 */}
        <DeviceTypeMultiSelect
          value={filters.deviceTypes || []}
          onChange={(value) =>
            handleFilterChange('deviceTypes', value.length > 0 ? value : undefined)
          }
          onAutoRefresh={onViewReport}
          className="w-[130px]"
        />

        {/* Period - Date Preset Menu with Custom Range */}
        <DatePresetMenu
          value={{ from: filters.dateFrom, to: filters.dateTo }}
          onChange={handlePeriodChange}
        />

        {/* View Report Button - Nayax Yellow */}
        <Button
          onClick={onViewReport || onRefresh}
          disabled={isLoading}
          className="bg-[#FFCD00] hover:bg-[#E6B800] text-[#262626] font-medium h-8 px-4 mt-auto shadow-sm"
        >
          {isLoading ? 'Loading...' : 'View Report'}
        </Button>

        {/* Reset Filters */}
        <Button
          variant="ghost"
          size="sm"
          onClick={handleResetFilters}
          className="text-xs h-8 px-2 text-[#606060] hover:text-[#262626] mt-auto"
          disabled={isLoading}
        >
          <RotateCcw className="h-3 w-3 mr-1" />
          Reset
        </Button>
      </div>

      {/* Row 2 - Secondary Search Filters: Specific lookups (HIVE-6245 updated layout) */}
      <div className="flex flex-wrap items-end gap-3 px-4 py-2">
        {/* Session ID - Most common direct lookup */}
        <TextFilterInput
          label="Session ID"
          value={filters.sessionId}
          onChange={(value) => handleFilterChange('sessionId', value as number | undefined)}
          onSubmit={onViewReport}
          type="number"
          className="w-[100px]"
        />

        {/* Charging Station - Combined CP Name + CP Number (HIVE-6245 #6) */}
        <TextFilterInput
          label="Charging Station"
          value={filters.chargingStation}
          onChange={(value) => handleFilterChange('chargingStation', value as string | undefined)}
          onSubmit={onViewReport}
          placeholder="Name or Number"
          className="w-[150px]"
        />

        {/* Physical Reference (HIVE-6245 #7) - Before Connector */}
        <TextFilterInput
          label="Physical Ref"
          value={filters.physicalReference}
          onChange={(value) => handleFilterChange('physicalReference', value as string | undefined)}
          onSubmit={onViewReport}
          className="w-[110px]"
        />

        {/* Connector ID */}
        <TextFilterInput
          label="Connector"
          value={filters.connectorId}
          onChange={(value) => handleFilterChange('connectorId', value as number | undefined)}
          onSubmit={onViewReport}
          type="number"
          className="w-[90px]"
        />

        {/* Account - HIVE-6307: searches Name, ID, and External Code */}
        <TextFilterInput
          label="Account"
          value={filters.accountName}
          onChange={(value) => handleFilterChange('accountName', value as string | undefined)}
          onSubmit={onViewReport}
          placeholder="Name, ID, Code..."
          className="w-[130px]"
        />

        {/* Payment Method */}
        <PaymentMethodDropdown
          value={filters.paymentMethod as any}
          onChange={(value) => handleFilterChange('paymentMethod', value)}
          onAutoRefresh={onViewReport}
          className="w-[130px]"
        />

        {/* Payment ID (renamed from Nayax Txn) - HIVE-6245 #8: moved after Payment Method */}
        <TextFilterInput
          label="Payment ID"
          value={filters.nayaxTransactionId}
          onChange={(value) => handleFilterChange('nayaxTransactionId', value as number | undefined)}
          onSubmit={onViewReport}
          type="number"
          className="w-[100px]"
        />

        {/* Token ID */}
        <TextFilterInput
          label="Token ID"
          value={filters.tokenId}
          onChange={(value) => handleFilterChange('tokenId', value as string | undefined)}
          onSubmit={onViewReport}
          className="w-[120px]"
        />

        {/* Last 4 Digits - HIVE-6307 */}
        <TextFilterInput
          label="Last 4 Digits"
          value={filters.last4Digits}
          onChange={(value) => handleFilterChange('last4Digits', value as string | undefined)}
          onSubmit={onViewReport}
          placeholder="Last 4..."
          className="w-[100px]"
        />
      </div>

      {/* Bottom Row - Column Group Checkboxes (HIVE-6300: matches Flutter) */}
      <div className="flex flex-wrap items-center gap-4 px-4 py-2">
        {/* Column Group Checkboxes with conditional visibility */}
        {columnGroups.map((group) => {
          // Conditional visibility: buyInfo only shows when paymentInfo is enabled
          // vat only shows when cost is enabled (matches Flutter behavior)
          if (group.dependsOn && !enabledColumnGroups.includes(group.dependsOn)) {
            return null;
          }

          // Fees checkbox is disabled when Live Transactions is ON (matches Flutter)
          const isDisabled = group.id === 'cost' && filters.liveTransactions;

          return (
            <label
              key={group.id}
              className={cn(
                'flex items-center gap-1.5',
                isDisabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
              )}
            >
              <Checkbox
                checked={enabledColumnGroups.includes(group.id)}
                onCheckedChange={(checked) =>
                  onColumnGroupChange(group.id, checked === true)
                }
                disabled={isDisabled}
                className="h-4 w-4 data-[state=checked]:bg-[#FFCD00] data-[state=checked]:border-[#FFCD00] data-[state=checked]:text-[#262626]"
              />
              <span className="text-sm text-[#262626]">{group.label}</span>
            </label>
          );
        })}

        {/* Spacer */}
        <div className="flex-1" />

        {/* Session Count */}
        {totalCount !== undefined && (
          <span className="text-sm text-[#606060] font-medium">{totalCount.toLocaleString()} sessions</span>
        )}

        {/* Live Transactions Toggle */}
        <div className="flex items-center gap-2">
          <Switch
            id="live-transactions"
            checked={filters.liveTransactions || false}
            onCheckedChange={handleLiveTransactionsChange}
            className="data-[state=checked]:bg-[#FFCD00]"
          />
          <label
            htmlFor="live-transactions"
            className="text-sm text-[#262626] cursor-pointer"
          >
            Live Transactions
          </label>
        </div>

        {/* Export Menu with Column Selection - HIVE-6307 */}
        {onExport && (
          <ExportMenu
            columnVisibility={columnVisibility}
            availableColumns={availableColumns}
            onExport={onExport}
            disabled={isLoading}
            isExporting={isExporting}
            exportProgress={exportProgress}
          />
        )}
      </div>
    </div>
  );
}
